import { useEffect, useRef } from 'react'

import { getValuesFromVariables } from '@helpers'
import { ACTION_CODE, ENTITY, EVENT_CODE } from '@constants'
import { ActionRow, GETAllVariables, ObjectDataRecord } from '@types'

import { EVENT_NAME, LocalEventBus, publish } from '../events'

interface UseChangeVariableActionsProps {
  selectedRows?: ObjectDataRecord[]
  objectData?: ObjectDataRecord | ObjectDataRecord[]
  hasExternalActionData?: boolean
  actions?: ActionRow[]
  entityId?: string | number
  outerVariables?: GETAllVariables
  outerEventBus?: LocalEventBus
  type?: ENTITY
}

const evaluateJsValue = (jsValue: string | undefined, context: Record<string, unknown>) => {
  if (!jsValue) return context
  try {
    return new Function('srcObj', jsValue)(context)
  } catch (error) {
    console.error('Error evaluating jsValue script:', error)

    return context
  }
}

const publishChangeVariable = (
  variable: ActionRow['variable'],
  value: unknown,
  entityId?: string | number
) => {
  const newVariableValue = {
    ...variable,
    entityId,
    value,
    updatedOn: new Date().toISOString(),
  }

  publish(EVENT_NAME.CHANGE_VARIABLE_VALUE, newVariableValue)
}

const publishChangeVariableForWindow = (
  jsValue: string,
  value: unknown,
  outerVariables: GETAllVariables,
  outerEventBus?: LocalEventBus
) => {
  const prepareOuterVariables = outerVariables.reduce(
    (acc: Record<string | number, any>, variable) => {
      acc[variable.internalId] = variable

      return acc
    },
    {}
  )

  const srcObj = { outerVariables: prepareOuterVariables }

  const currentVariable = evaluateJsValue(jsValue, srcObj)

  const newVariable = {
    ...currentVariable,
    defaultValue: value,
    updatedOn: new Date().toISOString(),
  }

  if (outerEventBus) {
    outerEventBus.publish(EVENT_NAME.CHANGE_VARIABLE_VALUE, newVariable)

    return
  }

  publish(EVENT_NAME.CHANGE_VARIABLE_VALUE, newVariable)
}

export const useChangeVariableActions = ({
  selectedRows,
  objectData,
  actions,
  entityId,
  outerVariables,
  outerEventBus,
  type,
}: UseChangeVariableActionsProps) => {
  const isInitialRef = useRef(true)

  useEffect(() => {
    if (!actions) return

    const rowSelectAction = actions.find(
      action =>
        action.actionCode === ACTION_CODE.CHANGE_VARIABLE_VALUE &&
        action.eventCode === EVENT_CODE.ON_ROW_SELECTION
    )

    if (rowSelectAction?.variable) {
      publishChangeVariable(rowSelectAction.variable, selectedRows, entityId)
    }

    if (rowSelectAction?.jsValue && outerVariables) {
      publishChangeVariableForWindow(
        rowSelectAction.jsValue,
        selectedRows,
        outerVariables,
        outerEventBus
      )
    }
  }, [selectedRows, actions, entityId, outerEventBus])

  useEffect(() => {
    if (!actions) return

    if (isInitialRef.current && type === ENTITY.LIST_CONTROL) {
      isInitialRef.current = false

      actions.forEach(action => {
        if (action.eventCode !== EVENT_CODE.ON_VARIABLE_CHANGE || !action.variable) return

        const preparedVariables = getValuesFromVariables(outerVariables)

        const variablesIncludesTargetVariable = action.variable?.internalId in preparedVariables

        if (!variablesIncludesTargetVariable) return

        const valueVariable = preparedVariables[action.variable?.internalId]

        if (!valueVariable) return

        publishChangeVariable(action.variable, valueVariable, entityId)
      })
    }

    const afterLoadAction = actions.find(
      actions =>
        actions.actionCode === ACTION_CODE.CHANGE_VARIABLE_VALUE &&
        actions.eventCode === EVENT_CODE.AFTER_LOAD_DATA
    )

    const onInitFormAction = actions.find(
      action =>
        action.actionCode === ACTION_CODE.CHANGE_VARIABLE_VALUE &&
        action.eventCode === EVENT_CODE.ON_INIT_FORM
    )

    if (afterLoadAction?.variable) {
      const value = evaluateJsValue(afterLoadAction.jsValue, {
        objectData,
        outerVariables: outerVariables ? getValuesFromVariables(outerVariables) : {},
      })
      publishChangeVariable(afterLoadAction.variable, value, entityId)
    }

    if (onInitFormAction?.variable) {
      const value = evaluateJsValue(onInitFormAction.jsValue, {
        row: objectData,
        outerVariables: outerVariables ? getValuesFromVariables(outerVariables) : {},
      })

      publishChangeVariable(onInitFormAction.variable, value, entityId)
    }
  }, [objectData, actions, entityId, outerVariables])
}
